
import { Component, Vue, Prop, Watch, Inject } from "vue-property-decorator";
import { namespace, Getter, Action } from "vuex-class";
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import ViewsEventBus from "@/services/views/viewsEventBus";

const MapLayers = namespace("mapLayers");

@Component({
	components: {
		"vue-perfect-scrollbar": VuePerfectScrollbar
	}
})
export default class MapLayersSelect extends Vue {
	@Action public fetchUserGroups;
	@Getter("getUserGroups") public groups;

	@MapLayers.Getter("getMapLayers") public mapLayers;
	@MapLayers.Getter("getZoomLevel") public zoomLevel;
	@MapLayers.Getter("getLayersHidden") public mapLayersHidden;

	@Inject("viewsEventBus") private eventBus!: ViewsEventBus;

	public indexedGroups: {};
	public zonesOpen: boolean = true;

	public groupsCollapsed = new Set<string>();
	public groupedLayers: any[] = [];

	public filter: string = "";

	// On mount, get groups and layers
	public async mounted() {
		await this.fetchUserGroups();
		this.mapLayersUpdated(this.mapLayers);
	}

	// List of distinct group IDs in visible map layers
	get groupsById() {
		if (this.indexedGroups == null) {
			this.indexedGroups = this.groups.reduce((acc, group) => {
				acc[group.groupID.toString()] = group;
				return acc;
			}, {});
		}

		return this.indexedGroups;
	}

	// When map layers are updated, break down by group ID
	@Watch("mapLayers")
	public mapLayersUpdated(value: any) {
		if (!this.mapLayers || !this.mapLayers.layers) {
			return null;
		}

		// Group layers by group ID
		const groupLayers = this.mapLayers.layers.reduce((acc, layer) => {
			(acc[layer.groupID] = acc[layer.groupID] || []).push(layer);
			return acc;
		}, {});

		const groupsById = this.groupsById;
		this.groupedLayers = [];
		// Get groups for grouped layers
		for (const groupID in groupLayers) {
			const groupedLayer = {
				group: groupsById[groupID.toString()],
				groupID,
				layers: groupLayers[groupID],
				open: !this.groupsCollapsed.has(groupID)
			};

			this.groupedLayers.push(groupedLayer);
		}
	}

	// Filters list of group layers by either group title or layer title
	get filteredGroupedLayers() {
		if (this.filter === "") {
			return this.groupedLayers;
		}

		const filter = this.filter.toLocaleLowerCase();

		return this.groupedLayers.filter(groupLayer => {
			return (
				groupLayer.group.title.toLocaleLowerCase().indexOf(filter) > -1 ||
				groupLayer.layers.some((layer: any) => layer.title.toLocaleLowerCase().indexOf(filter) > -1)
			);
		});
	}

	// Gets list of filtered layers by title
	public filteredLayers(groupLayer) {
		if (this.filter === "") {
			return groupLayer.layers;
		}

		const filter = this.filter.toLocaleLowerCase();
		if (groupLayer.group.title.toLocaleLowerCase().indexOf(filter) > -1) {
			return groupLayer.layers;
		} else {
			return groupLayer.layers.filter(layer => layer.title.toLocaleLowerCase().indexOf(filter) > -1);
		}
	}

	// Uncollapses all grouped map layers
	public openAll(group) {
		group.open = !group.open;
		if (group.open) {
			this.groupsCollapsed.delete(group.groupID.toString());
		} else {
			this.groupsCollapsed.add(group.groupID.toString());
		}
	}

	// Raise event to go to a map layer's bounds
	public gotoLayer(layer: any) {
		this.eventBus.$emit("gotoLayer", layer);
	}
}
